import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { fetchJewellersSidebarRequest } from "../../../redux/JewellerList/JewellerSlice";
import {
  createCouponRequest,
  updateCouponRequest,
} from "../../../redux/sagas/Coupon/CouponSlice";
import { CalculationType } from "../../../configs/constants";
import dayjs from "dayjs";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Example data for multi-select
const availableItems = ["User A", "User B", "User C", "User D"];

const generateCouponCode = () =>
  Math.random().toString(36).substring(2, 10).toUpperCase();

export default function CouponModal({ open, onClose, onSubmit, initialData }) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [targetType, setTargetType] = useState("all"); // "all" or "specific"
  const [couponValue, setCouponValue] = useState("");
  const [calculationType, setCalculationType] = useState("fixed");
  const [Jeweller, setJeweller] = useState("");
  const [error, setError] = useState("");
  const [couponCodeError, setCouponCodeError] = useState("");

  const dispatch = useDispatch();

  const { jewellersSidebar = [], loading } = useSelector(
    (state) => state.jeweller,
  );

  const handleCouponChange = (e) => {
    const value = Number(e.target.value);

    if (value < 0) {
      setError("Value cannot be less than 0");
      // setCouponValue(value); // keep what user typed so they can fix it
    } else {
      setError("");
      setCouponValue(value);
    }
  };
  const handleCouponCodeChange = (e) => {
    const value = e.target.value.toUpperCase();

    setCouponCode(value);

    if (value.length > 0 && value.length < 5) {
      setCouponCodeError("Coupon code must be at least 5 characters");
    } else if (value.length > 21) {
      setCouponCodeError("Coupon code cannot exceed 21 characters");
    } else {
      setCouponCodeError("");
    }
  };
  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest({ allActive: true }));
  }, []);

  // If editing, preload data
  useEffect(() => {
    if (initialData) {
      setSelectedItems(initialData.jewellers || []);
      setCouponCode(initialData.code || "");
      setExpiryDate(initialData.expiry ? dayjs(initialData.expiry) : null);
      setTargetType(initialData.targetType || "all");
      setCouponValue(initialData.value || "");
      setCalculationType(initialData.calculationType || "fixed");

      console.log("inside model", initialData);
    } else {
      setSelectedItems([]);
      setCouponCode("");
      setExpiryDate(null);
      setTargetType("all");
      setCouponValue("");
      setCalculationType("fixed");
    }
  }, [initialData]);

  const handleSubmit = () => {
    if (!couponCode || !expiryDate)
      return toast.error("Fill all required fields");
    if (targetType === "specific" && selectedItems.length === 0)
      return toast.error("Please select at least one user");
    if (calculationType == CalculationType.Percentage && couponValue > 100) {
      return toast.error("Percentage Value Should Be Not Greater Than 100");
    }
    if (couponCode.length < 5 || couponCode.length > 21) {
      return toast.error("Coupon code must be between 5 and 21 characters");
    }

    const payload = {
      targetType: targetType,
      jeweller: targetType === "specific" ? selectedItems : [],
      code: couponCode,
      expiry: expiryDate ? expiryDate.valueOf() : null,
      value: couponValue,
      calculationType: calculationType,
      jewellers: selectedItems,
    };
    console.log("payload", payload);

    if (initialData) {
      dispatch(updateCouponRequest({ ...initialData, ...payload }));
    } else {
      dispatch(createCouponRequest(payload));
    }

    onClose();

    // onSubmit({
    //   targetType,
    //   items: targetType === "specific" ? selectedItems : [],
    //   code: couponCode,
    //   expiry: expiryDate,
    // });
    // onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LocalOfferIcon color="primary" />
        {initialData ? "Update Coupon" : "Create Coupon"}
      </DialogTitle>
      <DialogContent>
        {/* Radio for All / Specific */}
        <FormControl component="fieldset" sx={{ mt: 1 }}>
          <FormLabel>Coupon Target</FormLabel>
          <RadioGroup
            row
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All Users"
            />
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label="Specific Users"
            />
          </RadioGroup>
        </FormControl>

        {/* Show user list only if specific */}
        {targetType === "specific" && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Users</InputLabel>
            <Select
              multiple
              value={selectedItems}
              onChange={(e) => setSelectedItems(e.target.value)}
              input={<OutlinedInput label="Select Users" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    const jeweller = jewellersSidebar.find(
                      (item) => item.id === value,
                    );
                    return (
                      <Chip key={jeweller.id} label={jeweller.jewellerName} />
                    );
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {jewellersSidebar.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.jewellerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Coupon Code with Generate Button */}
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <TextField
            fullWidth
            label="Coupon Code"
            value={couponCode}
            onChange={handleCouponCodeChange}
            required
            error={Boolean(couponCodeError)}
            helperText={couponCodeError}
          />
          <IconButton
            color="primary"
            onClick={() => setCouponCode(generateCouponCode())}
          >
            <AutorenewIcon />
          </IconButton>
        </Box>

        {/* Calculation Type */}
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel>Calculation Type</InputLabel>
          <Select
            required
            value={calculationType}
            onChange={(e) => setCalculationType(e.target.value)}
            label="Calculation Type"
          >
            {Object.values(CalculationType).map((item) => {
              return <MenuItem value={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
        {/* Coupon Value */}
        <TextField
          required
          fullWidth
          type="number"
          label="Coupon Value"
          value={couponValue}
          onChange={handleCouponChange}
          error={Boolean(error)}
          helperText={error}
          inputProps={{ min: 0 }}
          sx={{ mt: 2 }}
        />
        {/* Expiry DateTime */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Expiry Date & Time"
            required
            value={expiryDate}
            onChange={(newValue) => setExpiryDate(newValue)}
            sx={{ mt: 2, width: "100%" }}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {initialData ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
